import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import Places from 'constants/places'
import { usePlaceByName } from 'hooks/usePlace'
import { formatRoundedAmountWithDollarType } from 'lib/format/formatCurrencyIntl'
import type { DrawerExperienceSearchTile } from '../components/ExperienceListingDrawer/ExperienceDetailView'
import useExperienceSearch from '../components/ExperienceListingDrawer/useExperienceSearch'

interface UsePlaceThingsToDoOptions {
  placeName: string
  countryName?: string
  enabled: boolean
}

export interface UsePlaceThingsToDoResult {
  experiences: Array<DrawerExperienceSearchTile>
  experiencesCount: number
  countLabel: string
  lowestPricedExperience: DrawerExperienceSearchTile | undefined
  formattedLowestPrice: string | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

function usePlaceThingsToDo({
  placeName,
  countryName,
  enabled,
}: UsePlaceThingsToDoOptions): UsePlaceThingsToDoResult {
  const intl = useIntl()
  const geo = useContext(GeoContext)
  const currentCurrency = useContext(CurrencyContext)

  const searchQuery = useMemo(() => {
    return countryName ? `${placeName}, ${countryName}` : placeName
  }, [countryName, placeName])
  const resolvedPlace = usePlaceByName(searchQuery)
  const resolvedPlaceId = resolvedPlace?.id && resolvedPlace.id !== Places.Anywhere.id ? resolvedPlace.id : undefined

  const {
    experiences,
    isLoading,
    isError,
    refetch,
  } = useExperienceSearch({
    searchQuery,
    placeId: resolvedPlaceId,
    enabled: enabled && !!resolvedPlaceId,
  })

  const experiencesCount = experiences.length

  const countLabel = intl.formatMessage(
    {
      id: '4DlQKlqzVYbFq6Clh35W',
      defaultMessage: '{count, plural, one {# experience available} other {# experiences available}}',
      description: 'Subtitle on the nearby experiences overview card showing how many experience offers are available for the current place.',
    },
    { count: experiencesCount },
  )

  const lowestPricedExperience = useMemo(() => {
    return experiences.reduce<DrawerExperienceSearchTile | undefined>((lowest, experience) => {
      if (experience.price === undefined) {
        return lowest
      }

      if (lowest?.price === undefined || experience.price < lowest.price) {
        return experience
      }

      return lowest
    }, undefined)
  }, [experiences])

  const formattedLowestPrice = useMemo(() => {
    if (lowestPricedExperience?.price === undefined) {
      return undefined
    }

    return formatRoundedAmountWithDollarType(
      lowestPricedExperience.price,
      lowestPricedExperience.currencyCode ?? currentCurrency,
      geo.currentRegionCode,
    )
  }, [lowestPricedExperience, currentCurrency, geo.currentRegionCode])

  return {
    experiences,
    experiencesCount,
    countLabel,
    lowestPricedExperience,
    formattedLowestPrice,
    isLoading,
    isError,
    refetch,
  }
}

export default usePlaceThingsToDo
